/**
 * Fitness Service
 * Handles exercise_logs, body_data, and daily_goals management
 */

import {
  ExerciseLogsRecord,
  BodyDataRecord,
  DailyGoalsRecord,
} from '@/types/collections';
import { createRepository } from '@/services/repository';

class FitnessService {
  private exerciseRepo = createRepository<ExerciseLogsRecord>('exercise_logs');
  private bodyDataRepo = createRepository<BodyDataRecord>('body_data');
  private goalsRepo = createRepository<DailyGoalsRecord>('daily_goals');

  setUserId(userId: string): void {
    this.exerciseRepo.setUserId(userId);
    this.bodyDataRepo.setUserId(userId);
    this.goalsRepo.setUserId(userId);
  }

  // Exercise Logs API
  async listExercises(options?: { days?: number }) {
    const filter = options
      ? `date >= "${new Date(Date.now() - options.days! * 86400000).toISOString().split('T')[0]}"`
      : undefined;

    return this.exerciseRepo.list({
      filter,
      sort: '-date',
    });
  }

  async logExercise(data: Partial<ExerciseLogsRecord>) {
    return this.exerciseRepo.create(data);
  }

  async updateExerciseLog(id: string, data: Partial<ExerciseLogsRecord>) {
    return this.exerciseRepo.update(id, data);
  }

  async deleteExerciseLog(id: string) {
    return this.exerciseRepo.delete(id);
  }

  // Body Data API
  async getBodyData(options?: { days?: number }) {
    const filter = options
      ? `date >= "${new Date(Date.now() - options.days! * 86400000).toISOString().split('T')[0]}"`
      : undefined;

    return this.bodyDataRepo.list({
      filter,
      sort: '-date',
    });
  }

  async recordBodyData(data: Partial<BodyDataRecord>) {
    return this.bodyDataRepo.create(data);
  }

  async updateBodyData(id: string, data: Partial<BodyDataRecord>) {
    return this.bodyDataRepo.update(id, data);
  }

  // Daily Goals API
  async getGoals(date?: string) {
    const filter = date ? `date = "${date}"` : undefined;
    return this.goalsRepo.list({
      filter,
      sort: '-date',
    });
  }

  async setDailyGoals(data: Partial<DailyGoalsRecord>) {
    return this.goalsRepo.create(data);
  }

  async updateGoals(id: string, data: Partial<DailyGoalsRecord>) {
    return this.goalsRepo.update(id, data);
  }

  async deleteGoals(id: string) {
    return this.goalsRepo.delete(id);
  }
}

export const fitnessService = new FitnessService();
